import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { ROUTES } from '#/config/routes';
import { Badge } from '#/modules/commonComponents/badge';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import useDetailTable from '#/hooks/table/useDetailTable';
import { buildWorkOrderCreateUrlFromRequirement } from '#/modules/momMgmt/workOrder/helper/buildWorkOrderCreateUrlFromRequirement';
import {
  getMtrNoPrefix,
  hasRequirementShortage,
  isWorkOrderCreatePrefix,
  resolveRequirementRowKey,
} from '#/modules/momMgmt/workOrder/helper/materialRequirementPrefix';
import { formatDisplayQty } from '#/utils/formatNumber';

function formatMtrNo(row) {
  return row?.mtr?.mtrNo || '—';
}

function formatMtrName(row) {
  const name = row?.mtr?.name;
  if (!name) {
    return '—';
  }
  if (row?.substituted && row?.sourceMtr) {
    const sourceLabel = [row.sourceMtr.mtrNo, row.sourceMtr.name].filter(Boolean).join(' ');
    return sourceLabel ? `${name}（替代自 ${sourceLabel}）` : `${name}（已替代）`;
  }
  if (row?.substituted) {
    return `${name}（已替代）`;
  }
  return name;
}

function renderPurchaseCheckbox({ rowKey, original, selectedKeys, onToggleKey }) {
  const checked = selectedKeys.has(rowKey);
  return (
    <Checkbox
      checked={checked}
      aria-label={`選取 ${formatMtrNo(original)}`}
      onCheckedChange={(value) => {
        onToggleKey?.(rowKey, value === true);
      }}
    />
  );
}

function SubstitutedBadge({ row }) {
  const sourceLabel = [row?.sourceMtr?.mtrNo, row?.sourceMtr?.name]
    .filter(Boolean)
    .join(' ');
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" className="cursor-default">替代</Badge>
      </TooltipTrigger>
      <TooltipContent side="top">
        {sourceLabel ? `替代自 ${sourceLabel}` : '已替代'}
      </TooltipContent>
    </Tooltip>
  );
}

SubstitutedBadge.propTypes = {
  row: PropTypes.object.isRequired,
};

function buildColumns({
  selectedKeys,
  onToggleKey,
  purchasableKeys,
  selectAllChecked,
  onToggleAllPurchasable,
  workOrderId,
}) {
  const hasPurchasable = purchasableKeys.length > 0;

  return [
    {
      id: 'select',
      header: () => (
        <Checkbox
          checked={selectAllChecked}
          disabled={!hasPurchasable}
          aria-label="全選有短缺的物料"
          onCheckedChange={(value) => {
            onToggleAllPurchasable?.(value === true);
          }}
        />
      ),
      size: 52,
      minSize: 52,
      enableSorting: false,
      cell: ({ row }) => renderPurchaseCheckbox({
        rowKey: row.original._selectionKey,
        original: row.original,
        selectedKeys,
        onToggleKey,
      }),
    },
    {
      id: 'actions',
      header: '操作',
      size: 110,
      minSize: 100,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => {
        const original = row.original;
        const prefix = getMtrNoPrefix(original?.mtr?.mtrNo);
        if (!isWorkOrderCreatePrefix(prefix)) {
          return null;
        }
        return (
          <Button asChild size="sm" variant="outline">
            <Link
              to={buildWorkOrderCreateUrlFromRequirement(original, {
                fromWorkOrderId: workOrderId,
              })}
            >
              建立工單
            </Link>
          </Button>
        );
      },
    },
    {
      id: 'material',
      header: '物料',
      accessorFn: (row) => `${formatMtrNo(row)} ${formatMtrName(row)}`,
      size: 260,
      minSize: 200,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => {
        const original = row.original;
        const mtr = original?.mtr;
        return (
          <div className="flex min-w-0 items-center gap-2">
            <MaterialNameStack
              name={mtr?.name}
              code={mtr?.mtrNo}
              detailPath={mtr?.id ? ROUTES.mtrDetail(mtr.id) : null}
            />
            {original?.substituted ? <SubstitutedBadge row={original} /> : null}
          </div>
        );
      },
    },
    {
      id: 'satisfied',
      header: '是否滿足',
      accessorFn: (row) => row.satisfied,
      size: 80,
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue();
        if (value === true) {
          return <span className="font-semibold text-green-600">是</span>;
        }
        if (value === false) {
          return <span className="font-medium text-red-500">否</span>;
        }
        return <span className="text-muted-foreground">—</span>;
      },
    },
    {
      id: 'unitRequiredQty',
      header: '單位需求',
      accessorFn: (row) => formatDisplayQty(row.unitRequiredQty),
      size: 100,
      enableSorting: false,
    },
    {
      id: 'requiredQty',
      header: '需求量',
      accessorFn: (row) => formatDisplayQty(row.requiredQty),
      size: 100,
      enableSorting: false,
    },
    {
      id: 'issuedQty',
      header: '已出庫',
      accessorFn: (row) => formatDisplayQty(row.issuedQty),
      size: 100,
      enableSorting: false,
    },
    {
      id: 'lossQty',
      header: '損耗',
      accessorFn: (row) => formatDisplayQty(row.lossQty),
      size: 90,
      enableSorting: false,
    },
    {
      id: 'actualIssuedQty',
      header: '實際出庫',
      accessorFn: (row) => formatDisplayQty(row.actualIssuedQty),
      size: 100,
      enableSorting: false,
    },
    {
      id: 'remainingQty',
      header: '剩餘',
      accessorFn: (row) => formatDisplayQty(row.remainingQty),
      size: 90,
      enableSorting: false,
    },
    {
      id: 'shortageQty',
      header: '短缺',
      accessorFn: (row) => formatDisplayQty(row.shortageQty),
      size: 90,
      enableSorting: false,
      cell: ({ row }) => {
        const text = formatDisplayQty(row.original.shortageQty);
        return hasRequirementShortage(row.original)
          ? <span className="font-semibold text-red-600">{text}</span>
          : text;
      },
    },
  ];
}

const WorkOrderMaterialRequirementsTable = ({
  rows = [],
  selectedKeys = new Set(),
  onToggleKey,
  onToggleAllPurchasable,
  workOrderId,
}) => {
  const tableRows = useMemo(
    () => (rows ?? []).map((row, index) => ({
      ...row,
      _selectionKey: resolveRequirementRowKey(row, index),
      _rowKey: row?.id != null && row.id !== ''
        ? `${row.id}-${index}`
        : `material-req-${index}`,
    })),
    [rows],
  );

  const purchasableKeys = useMemo(
    () => tableRows
      .filter((row) => hasRequirementShortage(row))
      .map((row) => row._selectionKey),
    [tableRows],
  );

  const selectAllChecked = useMemo(() => {
    if (purchasableKeys.length === 0) {
      return false;
    }
    const selectedCount = purchasableKeys.filter((key) => selectedKeys.has(key)).length;
    if (selectedCount === 0) {
      return false;
    }
    if (selectedCount === purchasableKeys.length) {
      return true;
    }
    return 'indeterminate';
  }, [purchasableKeys, selectedKeys]);

  const columns = useMemo(
    () => buildColumns({
      selectedKeys,
      onToggleKey,
      purchasableKeys,
      selectAllChecked,
      onToggleAllPurchasable,
      workOrderId,
    }),
    [
      selectedKeys,
      onToggleKey,
      purchasableKeys,
      selectAllChecked,
      onToggleAllPurchasable,
      workOrderId,
    ],
  );

  const detailTableState = useDetailTable(tableRows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row._rowKey),
  });

  if (tableRows.length === 0) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">尚無資料</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

WorkOrderMaterialRequirementsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  selectedKeys: PropTypes.instanceOf(Set),
  onToggleKey: PropTypes.func,
  onToggleAllPurchasable: PropTypes.func,
  workOrderId: PropTypes.string,
};

export default WorkOrderMaterialRequirementsTable;
